'use client'

/**
 * Shared step-by-step view primitives.
 *
 * The flogger detail pages (`flogger-media-tabs.tsx`) render numbered,
 * illustrated steps with a user-selectable list / grid / carousel view.
 * These primitives were factored out so that renderer — and any future
 * one — shares a single implementation; tweaks (a new view mode, a layout
 * fix, an a11y improvement) flow to every surface that imports them.
 * (The scene page formerly used them too, via the now-removed
 * `cas-media-tabs.tsx`; its redesigned player renders steps directly.)
 *
 * The data shape is the lowest common denominator:
 *   { _key, imageUrl, stepInstructions?, additionalComment? }
 *
 * `stepInstructions` / `additionalComment` are localePortableText maps
 * ({ en: [blocks], … }) rendered through PortableTextRenderer so the
 * full rich-text formatting (bold, links, lists) editors author shows
 * on the public page.
 *
 * Callers that have a different shape (e.g. floggers' nested
 * `image: { url }` and locale-keyed strings) are responsible for
 * mapping into this shape before passing in.
 */
import { useCallback, useRef, useState, type TouchEvent as ReactTouchEvent } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useLocale } from 'next-intl'
import { pickPortableText } from '@/lib/i18n/pickLocale'
import { portableTextToPlain } from '@/lib/i18n/portableTextTranslation'
import { optimizeSanityImageUrl } from '@/lib/sanity/client'
import { PortableTextRenderer } from '@/components/content/portable-text-renderer'

/**
 * A localePortableText value — keyed by locale, each value an array of
 * Sanity portable-text blocks. Tolerates a bare block array too.
 */
type LocalePortableText =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Record<string, any[] | null | undefined> | any[] | null

export interface StepItem {
  _key: string
  imageUrl: string | null
  stepInstructions: LocalePortableText
  additionalComment: LocalePortableText
}

/** Plain-text fallback for an <img alt>, from a localePortableText value. */
function stepAltText(
  value: LocalePortableText,
  locale: string,
  index: number,
): string {
  const blocks = pickPortableText(value, locale)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const plain = portableTextToPlain(blocks as any).trim()
  return plain || `Step ${index + 1}`
}

function StepCard({
  step,
  index,
  onImageClick,
}: {
  step: StepItem
  index: number
  /** When set, the image becomes a click-to-enlarge button (opens the caller's lightbox). */
  onImageClick?: (step: StepItem) => void
}) {
  const locale = useLocale()
  const instructions = pickPortableText(step.stepInstructions, locale)
  const comment = pickPortableText(step.additionalComment, locale)
  const alt = stepAltText(step.stepInstructions, locale, index)
  return (
    <>
      {step.imageUrl &&
        (onImageClick ? (
          <button
            type="button"
            onClick={() => onImageClick(step)}
            aria-label="View image full size"
            className="group block w-full cursor-zoom-in overflow-hidden"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={optimizeSanityImageUrl(step.imageUrl, 800)}
              alt={alt}
              className="aspect-3/4 w-full bg-gray-100 object-cover transition-transform duration-200 group-hover:scale-105"
            />
          </button>
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={optimizeSanityImageUrl(step.imageUrl, 800)}
            alt={alt}
            className="aspect-3/4 w-full bg-gray-100 object-cover"
          />
        ))}
      <div className="p-3">
        {instructions.length > 0 && (
          <div className="prose prose-sm max-w-none text-gray-900 dark:text-gray-100">
            <PortableTextRenderer value={instructions as never} />
          </div>
        )}
        {comment.length > 0 && (
          <div className="prose prose-sm mt-1 max-w-none text-gray-600 dark:text-gray-400">
            <PortableTextRenderer value={comment as never} />
          </div>
        )}
      </div>
    </>
  )
}

type StepsProps = {
  steps: StepItem[]
  /** When set, step images become click-to-enlarge buttons calling this. */
  onImageClick?: (step: StepItem) => void
}

export function StepsList({ steps, onImageClick }: StepsProps) {
  return (
    <div className="space-y-6">
      {steps.map((step, i) => (
        <div key={step._key} className="flex gap-4 border p-4">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center bg-brand-700 text-sm font-bold text-white">
            {i + 1}
          </div>
          <div className="flex-1 space-y-2">
            <StepCard step={step} index={i} onImageClick={onImageClick} />
          </div>
        </div>
      ))}
    </div>
  )
}

export function StepsGrid({ steps, onImageClick }: StepsProps) {
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
      {steps.map((step, i) => (
        <div key={step._key} className="overflow-hidden border bg-white dark:bg-gray-900">
          <div className="relative">
            <StepCard step={step} index={i} onImageClick={onImageClick} />
            <span className="pointer-events-none absolute left-2 top-2 z-10 flex h-6 w-6 items-center justify-center bg-brand-700 text-xs font-bold text-white">
              {i + 1}
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

export function StepsCarousel({ steps, onImageClick }: StepsProps) {
  const locale = useLocale()
  const [current, setCurrent] = useState(0)
  const go = useCallback(
    (delta: number) =>
      setCurrent((prev) =>
        Math.max(0, Math.min(steps.length - 1, prev + delta)),
      ),
    [steps.length],
  )
  // Swipe navigation (mobile). Horizontal only, past a threshold — so a tap
  // still enlarges (when onImageClick is set) and vertical scrolls pass through.
  const touchStart = useRef<{ x: number; y: number } | null>(null)
  const onTouchStart = (e: ReactTouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
  }
  const onTouchEnd = (e: ReactTouchEvent) => {
    const start = touchStart.current
    touchStart.current = null
    if (!start) return
    const dx = e.changedTouches[0].clientX - start.x
    const dy = e.changedTouches[0].clientY - start.y
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1)
  }
  const step = steps[current]
  if (!step) return null

  const instructions = pickPortableText(step.stepInstructions, locale)
  const comment = pickPortableText(step.additionalComment, locale)

  return (
    <div>
      <div
        className="relative select-none overflow-hidden bg-white dark:bg-gray-950"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {step.imageUrl ? (
          onImageClick ? (
            <button
              type="button"
              onClick={() => onImageClick(step)}
              aria-label="View image full size"
              className="block w-full cursor-zoom-in"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={optimizeSanityImageUrl(step.imageUrl, 1600)}
                alt={stepAltText(step.stepInstructions, locale, current)}
                className="mx-auto block max-h-[600px] w-full object-contain"
                draggable={false}
              />
            </button>
          ) : (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={optimizeSanityImageUrl(step.imageUrl, 1600)}
              alt={stepAltText(step.stepInstructions, locale, current)}
              className="mx-auto block max-h-[600px] w-full object-contain"
              draggable={false}
            />
          )
        ) : (
          <div className="flex aspect-video w-full items-center justify-center text-4xl font-bold text-gray-300">
            {current + 1}
          </div>
        )}
        {steps.length > 1 && (
          <CarouselOverlay
            current={current}
            total={steps.length}
            go={go}
            // With click-to-enlarge, the image owns the click — so navigate via
            // edge arrow BUTTONS instead of full-half click zones (which would
            // swallow the enlarge tap).
            clickable={!!onImageClick}
          />
        )}
      </div>
      <div className="mt-4">
        <p className="text-xs font-medium text-brand-700">
          Step {current + 1} of {steps.length}
        </p>
        {instructions.length > 0 && (
          <div className="prose prose-sm mt-1 max-w-none text-gray-900 dark:text-gray-100">
            <PortableTextRenderer value={instructions as never} />
          </div>
        )}
        {comment.length > 0 && (
          <div className="prose prose-sm mt-1 max-w-none text-gray-600 dark:text-gray-400">
            <PortableTextRenderer value={comment as never} />
          </div>
        )}
      </div>
    </div>
  )
}

export function CarouselOverlay({
  current,
  total,
  go,
  clickable = false,
}: {
  current: number
  total: number
  go: (delta: number) => void
  /** Edge arrow BUTTONS (image handles its own click) instead of full-half click zones. */
  clickable?: boolean
}) {
  if (clickable) {
    return (
      <>
        <button
          type="button"
          onClick={() => go(-1)}
          disabled={current === 0}
          aria-label="Previous"
          className="absolute inset-y-0 left-0 z-20 flex w-12 items-center justify-center transition-opacity disabled:pointer-events-none disabled:opacity-0"
        >
          <span className="bg-black/40 p-2">
            <ChevronLeft className="h-6 w-6 text-white" />
          </span>
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          disabled={current === total - 1}
          aria-label="Next"
          className="absolute inset-y-0 right-0 z-20 flex w-12 items-center justify-center transition-opacity disabled:pointer-events-none disabled:opacity-0"
        >
          <span className="bg-black/40 p-2">
            <ChevronRight className="h-6 w-6 text-white" />
          </span>
        </button>
      </>
    )
  }
  return (
    <>
      <button
        onClick={() => go(-1)}
        disabled={current === 0}
        aria-label="Previous"
        className="absolute inset-y-0 left-0 w-1/2 cursor-pointer disabled:cursor-default"
      />
      <button
        onClick={() => go(1)}
        disabled={current === total - 1}
        aria-label="Next"
        className="absolute inset-y-0 right-0 w-1/2 cursor-pointer disabled:cursor-default"
      />
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 left-0 flex w-12 items-center justify-center transition-opacity ${
          current === 0 ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div className="bg-black/40 p-2">
          <ChevronLeft className="h-6 w-6 text-white" />
        </div>
      </div>
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 right-0 flex w-12 items-center justify-center transition-opacity ${
          current === total - 1 ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div className="bg-black/40 p-2">
          <ChevronRight className="h-6 w-6 text-white" />
        </div>
      </div>
    </>
  )
}
